import ContentElement from "./content-element.js";
import type { LightGallery as LightGalleryClass } from "lightgallery/lightgallery.js";
import type { LightGallerySettings } from "lightgallery/lg-settings.js";
import type { GalleryItem } from "lightgallery/lg-utils.js";

const DEFAULT_ELEMENT_SELECTOR: string = '.th-fsc-lg .light-gallery';

const defaultOptions: LightGallerySettings = {
    mode: 'lg-slide',
    easing: 'ease',
    speed: 400,
    licenseKey: '0000-0000-000-0000',
    height: '100%',
    width: '100%',
    addClass: '',
    startClass: 'lg-start-zoom',
    backdropDuration: 300,
    container: '',
    startAnimationDuration: 400,
    zoomFromOrigin: true,
    hideBarsDelay: 0,
    showBarsAfter: 10000,
    slideDelay: 0,
    supportLegacyBrowser: true,
    allowMediaOverlap: false,
    videoMaxSize: '1280-720',
    loadYouTubePoster: true,
    defaultCaptionHeight: 0,
    ariaLabelledby: '',
    ariaDescribedby: '',
    resetScrollPosition: true,
    hideScrollbar: false,
    closable: true,
    swipeToClose: true,
    closeOnTap: true,
    showCloseIcon: true,
    showMaximizeIcon: false,
    loop: true,
    escKey: true,
    keyPress: true,
    trapFocus: true,
    controls: true,
    slideEndAnimation: true,
    hideControlOnEnd: false,
    mousewheel: false,
    getCaptionFromTitleOrAlt: true,
    appendSubHtmlTo: '.lg-sub-html',
    subHtmlSelectorRelative: false,
    preload: 2,
    numberOfSlideItemsInDom: 10,
    selector: '',
    selectWithin: '',
    nextHtml: '',
    prevHtml: '',
    index: 0,
    iframeWidth: '100%',
    iframeHeight: '100%',
    iframeMaxWidth: '100%',
    iframeMaxHeight: '100%',
    download: true,
    counter: true,
    appendCounterTo: '.lg-toolbar',
    swipeThreshold: 50,
    enableSwipe: true,
    enableDrag: true,
    dynamic: true,
    dynamicEl: [],
    extraProps: [],
    exThumbImage: '',
    isMobile: undefined,
    mobileSettings: {
        controls: false,
        showCloseIcon: false,
        download: false,
    },
    plugins: [],
    strings: {
        closeGallery: 'Close gallery',
        toggleMaximize: 'Toggle maximize',
        previousSlide: 'Previous slide',
        nextSlide: 'Next slide',
        download: 'Download',
        playVideo: 'Play video',
        mediaLoadingFailed: 'Oops... Failed to load content...',
    }
};

class LightGallery extends ContentElement {
    protected options: LightGallerySettings;
    protected lightGalleryInstance: LightGalleryClass;

    public override init() {
        this.options = this.assignConfig<Partial<LightGallerySettings>>(defaultOptions, this.config, undefined, true);
    }

    public override process() {
        const self = this;

        const options = this.options;

        this.element.querySelectorAll<HTMLElement>('.items .properties').forEach((el: HTMLElement) => {
            const jsonEncoded = el.textContent ?? '';
            const galleryItem: GalleryItem = JSON.parse(jsonEncoded);
            options.dynamicEl?.push(galleryItem);
        });

        if(this.element.dataset.contentType && this.element.dataset.contentType == 'html') {
            options.appendSubHtmlTo = '.lg-item';
            let svg = '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 64 64" style="enable-background:new 0 0 64 64;" xml:space="preserve"><style type="text/css">.st0{fill:#5B5B5B;}.st1{fill:#FFFFFF;}.st2{font-family:"ArialMT";}.st3{font-size:48px;}</style><g><rect x="0.5" y="0.5" class="st0" width="63" height="63"/><path class="st1" d="M63,1v62H1V1H63 M64,0H0v64h64V0L64,0z"/></g><text transform="matrix(1 0 0 1 18.6526 47.8569)" class="st1 st2 st3">###NUMBER###</text></svg>';
            let c = 1;
            options.dynamicEl?.forEach((el) => {
                el.thumb = 'data:image/svg+xml;base64,'+btoa(svg.replace(/###NUMBER###/ig, c.toString()));
                c++;
            });
        }

        if(this.element.dataset.viewmode && this.element.dataset.viewmode == 'embed') {
            options.closable = false;
            options.escKey = false;
            document.body.style.overflowY = 'hidden';
            if((this.element.dataset.iframeBodyBackgroundColor ?? '') != '' ) {
                document.body.style.backgroundColor = this.element.dataset.iframeBodyBackgroundColor!;

            }

        }


        this.lightGalleryInstance = window.lightGallery(this.element, options)!;

        if(options.autoplay) {
            this.lightGalleryInstance.openGallery();
        } else {
            this.element.addEventListener('click', (ev: MouseEvent) => {
                self.lightGalleryInstance.openGallery();
            });
        }

        if(!options.closable) {
            this.element.addEventListener('onAfterOpen.lg', (ev) => {
                if(this.element.querySelector<HTMLElement>('.lg-close')) {
                    this.element.querySelector<HTMLElement>('.lg-close')?.remove();
                }
            });
        }

        if(window.location.href.search('#'+this.element.getAttribute('id')) > -1) {
            self.lightGalleryInstance.openGallery();
        }
    }

}

// Observe
LightGallery.initializeDom(DEFAULT_ELEMENT_SELECTOR, true, false, true);

export default LightGallery;
